import React, { useState, useEffect } from "react";
import { SEOHead } from "../components/SEOHead";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { apiEndpoints, API_BASE_URL } from "../utils/api";

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: "",
    privacy: false,
    newsletter: false,
  });

  const [formStatus, setFormStatus] = useState({
    loading: false,
    success: false,
    error: null,
  });

  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  // Cargar reCAPTCHA v3
  useEffect(() => {
    const loadRecaptcha = () => {
      if (window.grecaptcha) {
        setRecaptchaLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?render=6LdW17srAAAAAKQe7uRzBCrz0vawLidiKdFPIAmg';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        if (window.grecaptcha) {
          window.grecaptcha.ready(() => {
            setRecaptchaLoaded(true);
            //console.log('reCAPTCHA v3 cargado exitosamente');
          });
        }
      };
      script.onerror = () => {
        console.error('Error cargando reCAPTCHA v3');
      };

      document.head.appendChild(script);
    };

    loadRecaptcha();
    
    return () => {
      // Cleanup: remover script si es necesario
      const existingScript = document.querySelector('script[src*="recaptcha"]');
      if (existingScript && existingScript.parentNode) {
        existingScript.parentNode.removeChild(existingScript);
      }
    };
  }, []);

  // Cargar configuración del sitio
  useEffect(() => {
    const loadSiteSettings = async () => {
      try {
        const response = await fetch(apiEndpoints.settings.public());
        const data = await response.json();
        
        //console.log('Configuraciones cargadas:', data);
        
        if (data.success) {
          setSiteSettings(data.data);
        }
      } catch (error) {
        console.error('Error cargando configuraciones:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSiteSettings();
  }, []);

  // Información de contacto con datos dinámicos
  const contactInfo = {
    address: siteSettings?.contact?.address || "Calle 123 #45-67, Centro, Arauca, Colombia",
    phones: [
      { 
        label: "Principal", 
        number: siteSettings?.contact?.phone || "+57 123 456 7890" 
      }
    ],
    emails: [
      { 
        label: "General", 
        email: siteSettings?.contact?.email || "info@redarauca.com" 
      }
    ],
    schedule: {
      weekdays: siteSettings?.contact?.business_hours || "Lunes a Viernes: 8:00 AM - 6:00 PM",
      saturday: "Sábados: 9:00 AM - 2:00 PM",
      sunday: "Domingos: Cerrado",
    },
    socialMedia: siteSettings?.social || {},
  };

  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: "Contacto", url: "/contacto" },
  ];

  const subjectOptions = [
    { value: "", label: "Seleccione un asunto..." },
    { value: "informacion", label: "Solicitar información" },
    { value: "afiliacion", label: "Afiliación a medios" },
    { value: "colaboracion", label: "Propuesta de colaboración" },
    { value: "prensa", label: "Nota de prensa" },
    { value: "queja", label: "Queja o reclamo" },
    { value: "sugerencia", label: "Sugerencia" },
    { value: "otro", label: "Otro" },
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateForm = () => {
    const errors = [];

    if (!formData.firstName.trim()) errors.push("El nombre es requerido");
    if (!formData.lastName.trim()) errors.push("El apellido es requerido");
    if (!formData.email.trim()) errors.push("El email es requerido");
    if (!/\S+@\S+\.\S+/.test(formData.email))
      errors.push("El email no es válido");
    if (!formData.subject) errors.push("Debe seleccionar un asunto");
    if (!formData.message.trim()) errors.push("El mensaje es requerido");
    if (formData.message.trim().length < 10)
      errors.push("El mensaje debe tener al menos 10 caracteres");
    if (!formData.privacy)
      errors.push("Debe aceptar la política de privacidad");

    return errors;
  };

  const executeRecaptcha = async () => {
    if (!recaptchaLoaded || !window.grecaptcha) {
      throw new Error('reCAPTCHA no está disponible');
    }

    try {
      const token = await window.grecaptcha.execute('6LdW17srAAAAAKQe7uRzBCrz0vawLidiKdFPIAmg', {
        action: 'contact_form'
      });
      
      //console.log('Token reCAPTCHA obtenido:', token.substring(0, 50) + '...');
      return token;
    } catch (error) {
      //console.error('Error ejecutando reCAPTCHA:', error);
      throw new Error('Error de verificación de seguridad');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (errors.length > 0) {
      setFormStatus({
        loading: false,
        success: false,
        error: errors.join(", "),
      });
      return;
    }

    setFormStatus({ loading: true, success: false, error: null });

    try {
      // Ejecutar reCAPTCHA v3
      const recaptchaToken = await executeRecaptcha();
      
      // Agregar token al formulario
      const formDataWithRecaptcha = {
        ...formData,
        'g-recaptcha-response': recaptchaToken
      };

      // Enviar formulario a la API
      const response = await fetch(`${API_BASE_URL}/api/contact.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formDataWithRecaptcha),
      });

      const result = await response.json();
      //console.log('Respuesta del servidor:', result);

      if (result.success) {
        setFormStatus({
          loading: false,
          success: true,
          error: null,
        });

        // Limpiar formulario
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          company: "",
          subject: "",
          message: "",
          privacy: false,
          newsletter: false,
        });
      } else {
        setFormStatus({
          loading: false,
          success: false,
          error: result.message || "Error al enviar el mensaje. Intenta de nuevo.",
        });
      }
    } catch (error) {
      console.error('Error enviando formulario:', error);
      setFormStatus({
        loading: false,
        success: false,
        error: error.message || "Error de conexión. Por favor, intenta de nuevo.",
      });
    }
  };

  return (
    <>
      <SEOHead
        title="Contacto - Red Arauca"
        description="Contáctenos para consultas, sugerencias o propuestas de colaboración. Red Arauca, tu fuente de información confiable."
        keywords="contacto, red arauca, medios comunicación, arauca, consultas, colaboración"
      />

      <div className="contact-page">
        {/* Header con imagen de fondo */}
        <header className="page-header">
          <div className="header-container-left">
            <div className="header-content-left">
              <h1 className="display-4 fw-bold mb-0 mt-4">Contáctenos</h1>
              
              <div className="breadcrumbs-section-header">
                <Breadcrumbs items={breadcrumbItems} />
              </div>

              <div className="header-description">
                <p>Estamos aquí para atender tus consultas, sugerencias y comentarios</p>
              </div>
            </div>
          </div>
        </header>

        <div className="container">
          <div className="row">
            {/* Formulario de contacto */}
            <div className="col-lg-8">
              <div className="contact-form-container">
                {formStatus.success ? (
                  <SuccessMessage
                    onReset={() =>
                      setFormStatus({
                        loading: false,
                        success: false,
                        error: null,
                      })
                    }
                  />
                ) : (
                  <form onSubmit={handleSubmit} className="contact-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="firstName" className="form-label">
                          Nombre <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="lastName" className="form-label">
                          Apellido <span className="required">*</span>
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="email" className="form-label">
                          Email <span className="required">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="phone" className="form-label">
                          Teléfono
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="+57 123 456 7890"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="company" className="form-label">
                        Empresa/Organización
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="form-input"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="subject" className="form-label">
                        Asunto <span className="required">*</span>
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="form-select"
                        required
                      >
                        {subjectOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="message" className="form-label">
                        Mensaje <span className="required">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        className="form-textarea"
                        rows="5"
                        placeholder="Escribe tu mensaje aquí..."
                        required
                      ></textarea>
                      <div className="form-help">Mínimo 10 caracteres</div>
                    </div>

                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="privacy"
                          checked={formData.privacy}
                          onChange={handleInputChange}
                          className="form-checkbox"
                          required
                        />
                        <span className="checkbox-text">
                          Acepto la{" "}
                          <a href="/politica-privacidad" target="_blank">
                            política de privacidad
                          </a>{" "}
                          y el{" "}
                          <a href="/tratamiento-datos" target="_blank">
                            tratamiento de datos personales
                          </a>{" "}
                          <span className="required">*</span>
                        </span>
                      </label>
                    </div>

                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          name="newsletter"
                          checked={formData.newsletter}
                          onChange={handleInputChange}
                          className="form-checkbox"
                        />
                        <span className="checkbox-text">
                          Deseo recibir el boletín informativo y noticias relevantes
                        </span>
                      </label>
                    </div>

                    {/* Indicador de reCAPTCHA */}
                    <div className="recaptcha-info">
                      <p>
                        <i className="fas fa-shield-alt"></i>
                        Este formulario está protegido por reCAPTCHA v3.
                      </p>
                    </div>

                    {formStatus.error && (
                      <div className="form-error">
                        <i className="fas fa-exclamation-triangle"></i>
                        {formStatus.error}
                      </div>
                    )}

                    <button
                      type="submit"
                      className="submit-button"
                      disabled={formStatus.loading}
                    >
                      {formStatus.loading ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane"></i>
                          Enviar Mensaje
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>

            {/* Información de contacto */}
            <div className="col-lg-4">
              <div className="contact-contact-info">
                <h3>Información de Contacto</h3>

                {loading ? (
                  <div className="loading-info">
                    <i className="fas fa-spinner fa-spin"></i>
                    <p>Cargando información...</p>
                  </div>
                ) : (
                  <>
                    {/* Dirección */}
                    {contactInfo.address && (
                      <div className="contact-item-ctt">
                        <div className="contact-icon address">
                          <i className="fas fa-map-marker-alt"></i>
                        </div>
                        <div className="contact-details">
                          <h4>Dirección</h4>
                          <p>{contactInfo.address}</p>
                        </div>
                      </div>
                    )}

                    {/* Teléfonos */}
                    {contactInfo.phones.length > 0 && (
                      <div className="contact-item-ctt">
                        <div className="contact-icon phone">
                          <i className="fas fa-phone"></i>
                        </div>
                        <div className="contact-details">
                          <h4>Teléfonos</h4>
                          {contactInfo.phones.map((phone, index) => (
                            <p key={index}>
                              <strong>{phone.label}:</strong>{" "}
                              <a href={`tel:${phone.number}`}>{phone.number}</a>
                            </p>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Emails */}
                    {contactInfo.emails.length > 0 && (
                      <div className="contact-item-ctt">
                        <div className="contact-icon email">
                          <i className="fas fa-envelope"></i>
                        </div>
                        <div className="contact-details">
                          <h4>Correos Electrónicos</h4>
                          {contactInfo.emails.map((email, index) => (
                            <p key={index}>
                              <strong>{email.label}:</strong>{" "}
                              <a href={`mailto:${email.email}`}>{email.email}</a>
                            </p>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Horarios */}
                    <div className="contact-item-ctt">
                      <div className="contact-icon schedule">
                        <i className="fas fa-clock"></i>
                      </div>
                      <div className="contact-details">
                        <h4>Horario de Atención</h4>
                        <p>{contactInfo.schedule.weekdays}</p>
                        <p>{contactInfo.schedule.saturday}</p>
                        <p>{contactInfo.schedule.sunday}</p>
                      </div>
                    </div>

                    {/* Redes sociales */}
                    {Object.keys(contactInfo.socialMedia).length > 0 && (
                      <div className="contact-social">
                        <h4>Síguenos en Redes Sociales</h4>
                        <div className="contact-social-links">
                          {contactInfo.socialMedia.facebook && (
                            <a href={contactInfo.socialMedia.facebook} 
                               className="contact-social-link facebook" 
                               target="_blank" 
                               rel="noopener noreferrer">
                              <i className="fab fa-facebook-f"></i>
                            </a>
                          )}
                          {contactInfo.socialMedia.twitter && (
                            <a href={contactInfo.socialMedia.twitter} 
                               className="contact-social-link twitter"
                               target="_blank" 
                               rel="noopener noreferrer">
                              <i className="fab fa-twitter"></i>
                            </a>
                          )}
                          {contactInfo.socialMedia.instagram && (
                            <a href={contactInfo.socialMedia.instagram} 
                               className="contact-social-link instagram"
                               target="_blank" 
                               rel="noopener noreferrer">
                              <i className="fab fa-instagram"></i>
                            </a>
                          )}
                          {contactInfo.socialMedia.linkedin && (
                            <a href={contactInfo.socialMedia.linkedin} 
                               className="contact-social-link linkedin"
                               target="_blank" 
                               rel="noopener noreferrer">
                              <i className="fab fa-linkedin-in"></i>
                            </a>
                          )}
                          {contactInfo.socialMedia.youtube && (
                            <a href={contactInfo.socialMedia.youtube} 
                               className="contact-social-link youtube"
                               target="_blank" 
                               rel="noopener noreferrer">
                              <i className="fab fa-youtube"></i>
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <style jsx>{`
          .contact-page {
            background: #f8fafc;
            min-height: 100vh;
            padding: 0 0 60px;
          }

          .page-header {
            background: linear-gradient(
                to left,
                transparent 50%,
                rgba(0, 0, 0, 0.6) 100%
              ),
              url("https://img.freepik.com/free-vector/social-media-icons_53876-89123.jpg?t=st&w=2000");
            background-size: cover;
            background-position: bottom center;
            color: white;
            height: 220px;
            position: relative;
            display: flex;
            align-items: center;
            margin: 0;
          }

          .page-header::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.3);
          }

          .page-header .header-container-left {
            position: relative;
            z-index: 2;
            width: 100%;
            padding-left: 40px;
            padding-right: 20px;
          }

          .header-content-left {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            height: 100%;
            text-align: left;
            max-width: 600px;
          }

          .page-header h1 {
            font-size: 2.5rem;
            font-weight: 800;
            margin: 0;
            text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
            opacity: 0.95;
            line-height: 1.2;
          }

          .breadcrumbs-section-header {
            margin: 16px 0 8px 0;
          }

          .breadcrumbs-section-header nav {
            background: transparent !important;
            border: none !important;
            padding: 0 !important;
          }

          .breadcrumbs-section-header .breadcrumb-link {
            color: rgba(255, 255, 255, 0.9) !important;
            text-decoration: underline;
          }

          .breadcrumbs-section-header .breadcrumb-link:hover {
            color: white !important;
          }

          .breadcrumbs-section-header .breadcrumb-current {
            color: rgba(255, 255, 255, 0.8) !important;
            font-weight: 600;
          }

          .header-description {
            margin-top: 12px;
          }

          .header-description p {
            font-size: 1.1rem;
            color: rgba(255, 255, 255, 0.9);
            margin: 0;
            line-height: 1.5;
          }

          .container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 40px 20px 0;
          }

          .row {
            display: flex;
            gap: 30px;
            align-items: flex-start;
          }

          .col-lg-8 {
            flex: 1;
          }

          .col-lg-4 {
            flex: 0 0 350px;
          }

          .contact-form-container {
            background: white;
            border-radius: 16px;
            padding: 40px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }

          .contact-form {
            display: flex;
            flex-direction: column;
            gap: 20px;
          }

          .form-row {
            display: flex;
            gap: 20px;
          }

          .form-group {
            flex: 1;
            display: flex;
            flex-direction: column;
          }

          .form-label {
            font-weight: 600;
            color: #374151;
            margin-bottom: 8px;
            font-size: 0.9rem;
          }

          .required {
            color: #ef4444;
          }

          .form-input,
          .form-select {
            padding: 12px 16px;
            border: 2px solid #e5e7eb;
            border-radius: 8px;
            font-size: 1rem;
            color: #374151;
            transition: border-color 0.2s ease;
          }

          .form-input:focus,
          .form-select:focus {
            outline: none;
            border-color: #3b82f6;
          }

          .form-textarea {
            padding: 12px 16px;
            border: 2px solid #e5e7eb;
            border-radius: 8px;
            font-size: 1rem;
            color: #374151;
            resize: vertical;
            min-height: 120px;
            font-family: inherit;
            transition: border-color 0.2s ease;
          }

          .form-textarea:focus {
            outline: none;
            border-color: #3b82f6;
          }

          .form-help {
            font-size: 0.85rem;
            color: #6b7280;
            margin-top: 4px;
          }

          .checkbox-label {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            cursor: pointer;
            line-height: 1.5;
          }

          .form-checkbox {
            margin-top: 2px;
            flex-shrink: 0;
          }

          .checkbox-text {
            font-size: 0.9rem;
            color: #374151;
          }

          .checkbox-text a {
            color: #3b82f6;
            text-decoration: underline;
          }

          .recaptcha-info {
            background: #f9fafb;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 12px 16px;
            margin: 16px 0;
          }

          .recaptcha-info p {
            margin: 0;
            font-size: 0.85rem;
            color: #6b7280;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .recaptcha-info .fas.fa-shield-alt {
            color: #10b981;
          }

          .loading-text {
            color: #f59e0b;
            font-weight: 500;
          }

          .form-error {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 16px;
            background: #fef2f2;
            border: 1px solid #fecaca;
            border-radius: 8px;
            color: #dc2626;
            font-size: 0.9rem;
          }

          .submit-button {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 16px 32px;
            background: #3b82f6;
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 1rem;
            font-weight: 600;
            cursor: pointer;
            transition: background 0.2s ease;
            align-self: flex-start;
          }

          .submit-button:hover:not(:disabled) {
            background: #2563eb;
          }

          .submit-button:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .contact-contact-info {
            background: white;
            border-radius: 16px;
            padding: 30px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            position: sticky;
            top: 100px;
          }

          .contact-contact-info h3 {
            font-size: 1.5rem;
            font-weight: bold;
            color: #1f2937;
            margin-bottom: 25px;
            text-align: center;
          }

          .loading-info {
            text-align: center;
            padding: 40px 20px;
            color: #6b7280;
          }

          .loading-info i {
            font-size: 2rem;
            margin-bottom: 12px;
            display: block;
          }

          .contact-item-ctt {
            display: flex;
            gap: 15px;
            margin-bottom: 25px;
            padding-bottom: 20px;
            border-bottom: 1px solid #f3f4f6;
          }

          .contact-item-ctt:last-child {
            border-bottom: none;
            margin-bottom: 30px;
          }

          .contact-icon {
            width: 50px;
            height: 50px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 1.2rem;
            flex-shrink: 0;
          }

          .contact-icon.address {
            background: #3b82f6;
          }
          .contact-icon.phone {
            background: #10b981;
          }
          .contact-icon.email {
            background: #f59e0b;
          }
          .contact-icon.schedule {
            background: #8b5cf6;
          }

          .contact-details h4 {
            font-size: 1rem;
            font-weight: 600;
            color: #1f2937;
            margin-bottom: 8px;
          }

          .contact-details p {
            font-size: 0.9rem;
            color: #6b7280;
            margin-bottom: 4px;
            line-height: 1.4;
          }

          .contact-details a {
            color: #3b82f6;
            text-decoration: none;
          }

          .contact-details a:hover {
            text-decoration: underline;
          }

          .contact-social {
            border-top: 1px solid #f3f4f6;
            padding-top: 20px;
          }

          .contact-social h4 {
            font-size: 1rem;
            font-weight: 600;
            color: #1f2937;
            margin-bottom: 15px;
            text-align: center;
          }

          .contact-social-links {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
            justify-content: center;
          }

          .contact-social-link {
            width: 45px;
            height: 45px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            text-decoration: none;
            transition: transform 0.2s ease, box-shadow 0.2s ease;
          }

          .contact-social-link:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            color: white;
          }

          .contact-social-link.facebook {
            background: #1877f2;
          }
          .contact-social-link.twitter {
            background: #1da1f2;
          }
          .contact-social-link.instagram {
            background: #e4405f;
          }
          .contact-social-link.linkedin {
            background: #0077b5;
          }
          .contact-social-link.youtube {
            background: #ff0000;
          }

          @media (max-width: 768px) {
            .page-header {
              height: 180px;
            }

            .page-header .header-container-left {
              padding-left: 20px;
              padding-right: 16px;
            }

            .page-header h1 {
              font-size: 1.8rem;
            }

            .header-description p {
              font-size: 1rem;
            }

            .container {
              padding: 24px 16px 0;
            }

            .row {
              flex-direction: column;
            }

            .col-lg-4 {
              flex: none;
            }

            .contact-form-container {
              padding: 24px;
            }

            .form-row {
              flex-direction: column;
            }

            .contact-contact-info {
              position: static;
              margin-top: 20px;
            }
          }
        `}</style>
      </div>
    </>
  );
};

// Componente de mensaje de éxito
const SuccessMessage = ({ onReset }) => (
  <div className="success-message">
    <div className="success-icon">
      <i className="fas fa-check-circle"></i>
    </div>
    <h3>¡Mensaje enviado exitosamente!</h3>
    <p>
      Gracias por contactarnos. Hemos recibido tu mensaje y nos pondremos en
      contacto contigo lo antes posible.
    </p>
    <button onClick={onReset} className="reset-button">
      <i className="fas fa-envelope"></i>
      Enviar otro mensaje
    </button>

    <style jsx>{`
      .success-message {
        text-align: center;
        padding: 60px 20px;
        background: #f0fdf4;
        border: 2px solid #bbf7d0;
        border-radius: 12px;
      }

      .success-icon {
        font-size: 4rem;
        color: #10b981;
        margin-bottom: 20px;
      }

      .success-message h3 {
        color: #059669;
        font-size: 1.5rem;
        margin-bottom: 16px;
      }

      .success-message p {
        color: #047857;
        font-size: 1.1rem;
        line-height: 1.6;
        margin-bottom: 24px;
        max-width: 400px;
        margin-left: auto;
        margin-right: auto;
      }

      .reset-button {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 12px 24px;
        background: #10b981;
        color: white;
        border: none;
        border-radius: 8px;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.2s ease;
      }

      .reset-button:hover {
        background: #059669;
      }
    `}</style>
  </div>
);